// Group tile — the row in /manage that lists one of the caller's groups.
// Admins get an inline edit form + member management panel. Non-admins get
// the read-only summary + a Leave button.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/api';
import { timeAgo } from '../lib/format';
import type { BusinessMetadata, Group, GroupMember, GroupRole } from '../types/api';

interface Props {
    group: Group;
    myUserID: string;
    onChanged: () => void;
}

export function GroupTile({ group, myUserID, onChanged }: Props) {
    const [expanded, setExpanded] = useState(false);
    const isAdmin = group.my_role === 'admin';

    return (
        <div className={'tile' + (expanded ? ' is-expanded' : '')}>
            <div className="tile-head">
                <div className="tile-title">
                    <strong>{group.name}</strong>
                    <span className="tile-kind">{group.type}</span>
                    {group.my_role && (
                        <span className={'tile-role role-' + group.my_role}>{group.my_role}</span>
                    )}
                </div>
                <div className="tile-meta">{group.member_count} member{group.member_count === 1 ? '' : 's'}</div>
            </div>
            {group.description && <p className="tile-desc">{group.description}</p>}
            <div className="tile-actions">
                <span className="tile-slug">/{group.slug}</span>
                <button type="button" className="link" onClick={() => setExpanded(v => !v)}>
                    {expanded ? 'Close' : isAdmin ? 'Manage' : 'Details'}
                </button>
            </div>

            {expanded && (
                <div className="tile-expanded">
                    {isAdmin && (
                        <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
                            <Link to={'/groups/' + group.slug + '/dashboard'} className="link">
                                📊 View dashboard →
                            </Link>
                        </div>
                    )}
                    {isAdmin && <EditPanel group={group} onSaved={onChanged} />}
                    {isAdmin && group.type === 'business' && (
                        <BusinessEditor group={group} onSaved={onChanged} />
                    )}
                    <MembersPanel group={group} myUserID={myUserID} isAdmin={isAdmin} onChanged={onChanged} />
                    {!isAdmin && (
                        <LeaveButton group={group} onLeft={onChanged} />
                    )}
                </div>
            )}
        </div>
    );
}

// -------- Business metadata editor (admin, business groups only) --------
function BusinessEditor({ group, onSaved }: { group: Group; onSaved: () => void }) {
    const api = useApi();
    const meta: BusinessMetadata = (() => {
        try { return JSON.parse(JSON.stringify((group as { business_metadata?: unknown }).business_metadata ?? {})) as BusinessMetadata; }
        catch { return {}; }
    })();
    const [category, setCategory] = useState(meta.category ?? '');
    const [address,  setAddress]  = useState(meta.address  ?? '');
    const [phone,    setPhone]    = useState(meta.phone    ?? '');
    const [email,    setEmail]    = useState(meta.email    ?? '');
    const [website,  setWebsite]  = useState(meta.website  ?? '');
    const [hours,    setHours]    = useState(meta.hours    ?? '');
    const [status,   setStatus]   = useState<{ tone: 'ok' | 'err' | 'busy'; msg: string } | null>(null);

    async function onSubmit(e: FormEvent) {
        e.preventDefault();
        setStatus({ tone: 'busy', msg: 'Saving…' });
        try {
            await api.patch('/api/groups/' + group.slug, {
                business_metadata: {
                    category: category || undefined,
                    address:  address  || undefined,
                    phone:    phone    || undefined,
                    email:    email    || undefined,
                    website:  website  || undefined,
                    hours:    hours    || undefined,
                },
            });
            setStatus({ tone: 'ok', msg: '✓ Business details saved' });
            onSaved();
            setTimeout(() => setStatus(null), 1500);
        } catch (err) {
            setStatus({ tone: 'err', msg: (err as Error).message });
        }
    }
    return (
        <form className="tile-edit-form business-editor" onSubmit={onSubmit}>
            <h4>Business details</h4>
            <div className="business-grid">
                <label>
                    <span>Category</span>
                    <input type="text" placeholder="Cafe / Salon / Retail…" value={category} onChange={(e) => setCategory(e.target.value)} />
                </label>
                <label>
                    <span>Phone</span>
                    <input type="tel" placeholder="+27 21 …" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </label>
                <label style={{ gridColumn: '1 / -1' }}>
                    <span>Address</span>
                    <input type="text" placeholder="12 Bosch Ave, Vredehoek" value={address} onChange={(e) => setAddress(e.target.value)} />
                </label>
                <label>
                    <span>Email</span>
                    <input type="email" placeholder="hello@business.co.za" value={email} onChange={(e) => setEmail(e.target.value)} />
                </label>
                <label>
                    <span>Website</span>
                    <input type="url" placeholder="https://…" value={website} onChange={(e) => setWebsite(e.target.value)} />
                </label>
                <label style={{ gridColumn: '1 / -1' }}>
                    <span>Hours</span>
                    <input type="text" placeholder="Mon–Fri 8am–5pm · Sat 9am–1pm" value={hours} onChange={(e) => setHours(e.target.value)} />
                </label>
            </div>
            <div className="actions">
                <button type="submit" className="primary">Save business details</button>
                {status && (
                    <span className={status.tone === 'err' ? 'err' : status.tone === 'ok' ? 'ok' : ''}>{status.msg}</span>
                )}
            </div>
        </form>
    );
}

// -------- Edit panel (admin only) --------
function EditPanel({ group, onSaved }: { group: Group; onSaved: () => void }) {
    const api = useApi();
    const [name,        setName]        = useState(group.name);
    const [description, setDescription] = useState(group.description ?? '');
    const [active,      setActive]      = useState(group.active);
    const [status,      setStatus]      = useState<{ tone: 'ok' | 'err' | 'busy'; msg: string } | null>(null);

    async function onSubmit(e: FormEvent) {
        e.preventDefault();
        setStatus({ tone: 'busy', msg: 'Saving…' });
        try {
            await api.patch('/api/groups/' + group.slug, {
                name,
                description: description || null,
                active,
            });
            setStatus({ tone: 'ok', msg: '✓ Saved' });
            onSaved();
            setTimeout(() => setStatus(null), 1500);
        } catch (err) {
            setStatus({ tone: 'err', msg: (err as Error).message });
        }
    }

    return (
        <form className="tile-edit-form" onSubmit={onSubmit}>
            <h4>Edit</h4>
            <label>
                <span>Name</span>
                <input type="text" required value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label>
                <span>Description</span>
                <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
            <label className="inline-toggle">
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                <span>Active — untick to hide the group from listings without deleting it.</span>
            </label>
            <div className="actions">
                <button type="submit" className="primary">Save changes</button>
                {status && (
                    <span className={status.tone === 'err' ? 'err' : status.tone === 'ok' ? 'ok' : ''}>
                        {status.msg}
                    </span>
                )}
            </div>
        </form>
    );
}

// -------- Members panel --------
interface MembersProps {
    group:    Group;
    myUserID: string;
    isAdmin:  boolean;
    onChanged: () => void;
}

function MembersPanel({ group, myUserID, isAdmin, onChanged }: MembersProps) {
    const api = useApi();
    const [members, setMembers] = useState<GroupMember[]>([]);
    const [error,   setError]   = useState<string>('');

    const load = useCallback(async () => {
        try {
            const r = await api.get<{ members: GroupMember[] }>('/api/groups/' + group.slug + '/members');
            setMembers(r.members ?? []);
        } catch (e) {
            setError((e as Error).message);
        }
    }, [api, group.slug]);

    useEffect(() => { load(); }, [load]);

    async function changeRole(m: GroupMember, next: GroupRole) {
        try {
            await api.patch('/api/groups/' + group.slug + '/members/' + m.user_id, { role: next });
            await load();
            onChanged();
        } catch (e) { setError((e as Error).message); }
    }

    return (
        <div className="members-panel">
            <h4>Members</h4>
            {error && <p style={{ color: '#8b1919' }}>{error}</p>}
            {members.length === 0 && !error && <p style={{ color: '#999', fontStyle: 'italic' }}>Loading…</p>}
            {members.map(m => {
                const isMe = m.user_id === myUserID;
                return (
                    <div className="member-row" key={m.user_id}>
                        <span className="member-name">
                            {m.display_name}
                            {isMe && <span className="member-you"> (you)</span>}
                        </span>
                        <span className="member-joined">joined {timeAgo(m.joined_at)}</span>
                        {isAdmin && !isMe ? (
                            <select
                                className="member-role-select"
                                value={m.role}
                                onChange={(e) => changeRole(m, e.target.value as GroupRole)}
                            >
                                <option value="member">member</option>
                                <option value="publisher">publisher</option>
                                <option value="admin">admin</option>
                            </select>
                        ) : (
                            <span className={'group-role role-' + m.role}>{m.role}</span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// -------- Leave (non-admins) --------
function LeaveButton({ group, onLeft }: { group: Group; onLeft: () => void }) {
    const api = useApi();
    const [confirming, setConfirming] = useState(false);
    const [error,      setError]      = useState('');

    async function leave() {
        try {
            await api.post('/api/groups/' + group.slug + '/leave');
            onLeft();
        } catch (e) { setError((e as Error).message); }
    }

    return (
        <div className="leave-block">
            {!confirming ? (
                <button type="button" className="link danger" onClick={() => setConfirming(true)}>Leave this group</button>
            ) : (
                <div className="actions">
                    <span>Sure?</span>
                    <button type="button" className="primary danger" onClick={leave}>Yes, leave</button>
                    <button type="button" className="plain" onClick={() => setConfirming(false)}>Cancel</button>
                </div>
            )}
            {error && <p style={{ color: '#8b1919' }}>{error}</p>}
        </div>
    );
}
